// Collapsible recent-forms list grouped by template name.

import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { deleteOrgFormInstance } from "@/lib/opportunity/orgFormInstanceClient";
import type { OrgFormInstanceListItem } from "@/lib/opportunity/orgFormInstanceTypes";
import {
  canDeleteSupersededOrgFormInstance,
  groupOrgFormInstancesByTemplate,
  paginateOrgFormGroups,
  type OrgFormInstanceGroup,
} from "@/lib/opportunity/orgFormRecentGroups";

type Props = {
  instances: OrgFormInstanceListItem[];
  onOpenInstance: (instanceId: string) => void;
  onInstancesChanged: () => void;
  actionError: string | null;
  onActionError: (message: string | null) => void;
};

function FormInstanceRow({
  inst,
  group,
  isAdmin,
  deletingId,
  onOpen,
  onDelete,
}: {
  inst: OrgFormInstanceListItem;
  group: OrgFormInstanceGroup;
  isAdmin: boolean;
  deletingId: string | null;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const canDelete = canDeleteSupersededOrgFormInstance({ instance: inst, group, isAdmin });

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
      <div>
        <div className="text-xs text-gray-500 dark:text-zinc-400">
          {inst.status} · {inst.source_type} · updated {new Date(inst.updated_at).toLocaleString()}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onOpen}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium dark:border-zinc-600"
        >
          {inst.status === "saved" ? "View / re-save" : "Continue"}
        </button>
        {canDelete ? (
          <button
            type="button"
            disabled={deletingId === inst.id}
            onClick={onDelete}
            className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 disabled:opacity-50 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
          >
            {deletingId === inst.id ? "Deleting…" : "Delete"}
          </button>
        ) : null}
      </div>
    </li>
  );
}

function FormInstanceGroupBlock({
  group,
  expanded,
  onToggle,
  isAdmin,
  deletingId,
  onOpenInstance,
  onDeleteInstance,
}: {
  group: OrgFormInstanceGroup;
  expanded: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  deletingId: string | null;
  onOpenInstance: (instanceId: string) => void;
  onDeleteInstance: (instanceId: string) => void;
}) {
  const olderCount = group.older.length;

  return (
    <li className="border-b border-gray-100 last:border-b-0 dark:border-zinc-800">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/50"
      >
        <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">
          {expanded ? "▾" : "▸"} {group.displayName}
        </span>
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          {olderCount > 0
            ? `${olderCount + 1} versions · latest ${new Date(group.latest.updated_at).toLocaleDateString()}`
            : `updated ${new Date(group.latest.updated_at).toLocaleDateString()}`}
        </span>
      </button>
      <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
        <FormInstanceRow
          inst={group.latest}
          group={group}
          isAdmin={isAdmin}
          deletingId={deletingId}
          onOpen={() => onOpenInstance(group.latest.id)}
          onDelete={() => onDeleteInstance(group.latest.id)}
        />
        {expanded
          ? group.older.map((inst) => (
              <FormInstanceRow
                key={inst.id}
                inst={inst}
                group={group}
                isAdmin={isAdmin}
                deletingId={deletingId}
                onOpen={() => onOpenInstance(inst.id)}
                onDelete={() => onDeleteInstance(inst.id)}
              />
            ))
          : null}
      </ul>
    </li>
  );
}

export function FulfillmentFormsRecentBlock({
  instances,
  onOpenInstance,
  onInstancesChanged,
  actionError,
  onActionError,
}: Props) {
  const auth = useAuth();
  const isAdmin = auth.role === "admin" || auth.role === "owner" || auth.role === "dev";
  const [expandedTemplateIds, setExpandedTemplateIds] = useState<Set<string>>(new Set());
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const groups = useMemo(() => groupOrgFormInstancesByTemplate(instances), [instances]);
  const { visible, totalCount, hasMore } = useMemo(
    () => paginateOrgFormGroups(groups, undefined, showAllGroups),
    [groups, showAllGroups],
  );

  function toggleGroup(templateId: string) {
    setExpandedTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
  }

  async function handleDelete(instanceId: string) {
    if (!window.confirm("Delete this superseded draft? This cannot be undone.")) return;
    onActionError(null);
    setDeletingId(instanceId);
    const r = await deleteOrgFormInstance(instanceId);
    setDeletingId(null);
    if (!r.ok) {
      onActionError(r.error);
      return;
    }
    onInstancesChanged();
  }

  if (instances.length === 0) {
    return <p className="px-4 py-6 text-sm text-gray-500 dark:text-zinc-400">No forms started yet.</p>;
  }

  return (
    <div>
      {actionError ? (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {actionError}
        </div>
      ) : null}
      <ul>
        {visible.map((group) => (
          <FormInstanceGroupBlock
            key={group.templateId}
            group={group}
            expanded={expandedTemplateIds.has(group.templateId)}
            onToggle={() => toggleGroup(group.templateId)}
            isAdmin={isAdmin}
            deletingId={deletingId}
            onOpenInstance={onOpenInstance}
            onDeleteInstance={(id) => void handleDelete(id)}
          />
        ))}
      </ul>
      {hasMore ? (
        <div className="border-t border-gray-100 px-4 py-3 text-center dark:border-zinc-800">
          <button
            type="button"
            onClick={() => setShowAllGroups((v) => !v)}
            className="text-sm text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {showAllGroups ? "Show fewer form groups" : `Show all ${totalCount} form groups`}
          </button>
        </div>
      ) : null}
    </div>
  );
}
